import { useEffect, useState } from "react";
import {
	DialogContent,
	Typography,
	Box,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	CircularProgress,
	Stack,
} from "@mui/material";
import { SwapHorizOutlined } from "@mui/icons-material";
import {
	MoleCancelButton,
	MoleDialog,
	MoleDialogActions,
	MoleDialogHeader,
	MolePrimaryButton,
	useMoleThemeColor,
} from "../../components/MoleDialogShell";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faUser, faCalendarAlt, faHashtag, faPerson, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Util } from "../../util/Util";
import { useAppSocket } from "../../providers/AppSocketContext";


import { addMoney } from "../../util/quantity";
import { canExportPdf } from "../../util/access";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle: any;
}

const TransferReport = ({ isOpen, toggle, bundle }: Props) => {
	const [record, setRecord] = useState<any>(null);

	const { api, access } = useAuth();
	const { queueReRun, setLoadingMessage, t } = useUI();
	const { appSettings } = useAppSocket();
	const themeColor = useMoleThemeColor(appSettings?.UI_THEME_PRIMARY?.value);

	const fetchData = async () => {
		const res = await api.get(`records/${bundle.batch}`);
		if (res) {
			if (res.status === 200) {
				setRecord(res.data);
				return;
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchData);
			} else if (res.status === 404) {
				toast.warn(t("common:toast.warn.noRecord"));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}
		toggle();
	};

	const handlePrint = async () => {
		if (!canExportPdf(access, "movement")) return;
		setLoadingMessage("EXPORT");

		const res = await api.get(`reports/transfer/${bundle.batch}`);

		if (res) {
			if (res.status === 200) {
				const pdfData = atob(res.data.data);
				const blob = new Blob([Uint8Array.from(pdfData, c => c.charCodeAt(0))], { type: "application/pdf" });
				window.open(URL.createObjectURL(blob));
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setLoadingMessage(null);
	};
	
	useEffect(() => {
		if (isOpen && bundle) fetchData();
	}, [isOpen]);

	const total = record?.records?.reduce((acc: number, r: any) => addMoney(acc, (r?.total || 0)), 0) || 0;

	return (
		<MoleDialog open={isOpen} onClose={toggle} maxWidth="md" fullWidth themeColor={themeColor} paperOptions={{ maxHeight: "95vh" }}>
			<MoleDialogHeader
				eyebrow={t("common:report.stockTransferReport")}
				title={record?.details.reference || t("common:status.loading")}
				icon={<SwapHorizOutlined />}
				onClose={toggle}
				themeColor={themeColor}
			/>

			<DialogContent sx={{ p: 4 }}>
				{record ? (
					<Box>
						{/* Info Grid */}
						<Grid container spacing={4} sx={{ mb: 4 }}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography variant="overline" color="text.secondary" fontWeight={700}>
									{t("common:report.transferDetails")}
								</Typography>
								<Stack spacing={1} sx={{ mt: 1 }}>
									<DetailItem icon={faHashtag} label={t("common:tables.reference")} value={record?.details.reference} />
									<DetailItem icon={faCalendarAlt} label={t("common:tables.date")} value={Util.formatDate(record?.details.date, "full")} />
									<DetailItem icon={faPerson} label={t("common:tables.observerReceiver")} value={record?.details.client} />
									<DetailItem icon={faUser} label={t("common:report.recordedBy")} value={record?.details.user} />
								</Stack>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Typography variant="overline" color="text.secondary" fontWeight={700}>
									{t("common:report.transferRoute")}
								</Typography>
								<Box sx={{ mt: 1, p: 2, bgcolor: "action.hover", borderRadius: 2, border: "1px dashed", borderColor: "divider" }}>
									<Stack direction="row" alignItems="center" spacing={2} justifyContent="center">
										<Box textAlign="center">
											<Typography variant="caption" display="block" color="text.secondary">{t("common:report.source")}</Typography>
											<Typography variant="body1" fontWeight={600}>{record?.details.source}</Typography>
										</Box>
										<FontAwesomeIcon icon={faArrowRight} style={{ color: "#94a3b8" }} />
										<Box textAlign="center">
											<Typography variant="caption" display="block" color="text.secondary">{t("common:report.destination")}</Typography>
											<Typography variant="body1" fontWeight={600}>{record?.details.destination}</Typography>
										</Box>
									</Stack>
								</Box>
							</Grid>
						</Grid>

						{/* Items Table */}
						<TableContainer component={Paper} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
							<Table>
								<TableHead sx={{ bgcolor: "action.hover" }}>
									<TableRow>
											<TableCell sx={{ fontWeight: 700 }}>{t("common:tableView.detailPanel.description")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:tables.quantity")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:report.unitPrice")}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 700 }}>{t("common:tables.amount")}</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{record.records?.map((item: any, idx: number) => (
										<TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
											<TableCell sx={{ py: 1.5 }}>
												<Typography variant="body2" fontWeight={500}>{item.item_name}</Typography>
												<Typography variant="caption" color="text.secondary"># {t("common:report.code", { code: item.item_code || t("common:status.noDetails") })}</Typography>
											</TableCell>
											<TableCell align="right">{Util.formatNumber(item.quantity)}</TableCell>
											<TableCell align="right">{Util.formatNumber(item.amount)}</TableCell>
											<TableCell align="right" sx={{ fontWeight: 600 }}>{Util.formatNumber(item.total)}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>

						{/* Summary */}
						<Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", mt: 4, gap: 2 }}>
							<Box sx={{ maxWidth: 400 }}>
								{record.details?.description && (
									<>
										<Typography variant="subtitle2" fontWeight={700}>{t("common:report.notes")}</Typography>
										<Typography variant="body2" color="text.secondary">
											{record.details.description}
										</Typography>
									</>
								)}
							</Box>
							<Paper variant="outlined" sx={{ p: 2, minWidth: 260, bgcolor: appSettings?.UI_THEME_PRIMARY.value || "primary.main", color: "primary.contrastText", borderRadius: 2 }}>
								<Stack direction="row" justifyContent="space-between" alignItems="center">
									<Typography variant="body1" sx={{ opacity: 0.9 }}>{t("common:report.totalValue")}</Typography>
									<Typography variant="h5" fontWeight={800}>
										{Util.formatNumber(total)}
									</Typography>
								</Stack>
								<Typography variant="caption" sx={{ opacity: 0.8, display: "block", textAlign: "right", mt: 0.5 }}>
									{t("common:report.lineItemsIncluded", { count: record.records?.length || 0, num: record.records?.length || 0 })}
								</Typography>
							</Paper>
						</Box>
					</Box>
				) : (
					<Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
						<CircularProgress thickness={5} size={48} />
						<Typography sx={{ mt: 2, color: "text.secondary", fontWeight: 500 }}>
							{t("common:report.retrievingRecords")}
						</Typography>
					</Stack>
				)}
			</DialogContent>

			<MoleDialogActions>
				<MoleCancelButton onClick={toggle}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
				{canExportPdf(access, "movement") && (
					<MolePrimaryButton
						startIcon={<FontAwesomeIcon icon={faFilePdf} />}
						onClick={handlePrint}
						disabled={!record}
					>
						{t("common:ui.buttons.export")}
					</MolePrimaryButton>
				)}
			</MoleDialogActions>
		</MoleDialog>
	);
};

// Helper component for cleaner code
const DetailItem = ({ icon, label, value }: { icon: any, label: string, value: string }) => (
	<Stack direction="row" spacing={1.5} alignItems="center">
		<FontAwesomeIcon icon={icon} style={{ width: 14, color: "#64748b" }} />
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>{label}:</Typography>
		<Typography variant="body2" fontWeight={600}>{value || "—"}</Typography>
	</Stack>
);

export default TransferReport;